import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';
import Autocomplete from '@mui/joy/Autocomplete';
import IconButton from '@mui/joy/IconButton';
import Snackbar from '@mui/joy/Snackbar';
import {
    CheckCircle,
    Warning,
    Refresh,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    VisibilityOff
} from '@mui/icons-material';
import ApiService from '../../axios/Bookings-Axios';
import { GetAllFranchises } from '../../axios/Axios';

interface UnassignedLead {
    customer_id: number;
    customer_name: string;
    postcode: string;
    outward_postcode: string;
    email: string;
    phone: string;
    created_at: string;
    advertisement_platform: string;
    services: string[];
    lead_id: number | null;
    campaign_name: string;
}

interface Franchise {
    id: number;
    location: string;
}

// Postcode hover map component
const PostcodeMapHover = ({ postcode, children }: { postcode: string; children: React.ReactNode }) => {
    const [showMap, setShowMap] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const handleMouseEnter = (e: React.MouseEvent) => {
        setPosition({ x: e.clientX, y: e.clientY });
        setShowMap(true);
    };

    const handleMouseLeave = () => {
        setShowMap(false);
    };

    const getMapUrl = (pc: string) => {
        const encodedPostcode = encodeURIComponent(pc + ', UK');
        return `https://maps.google.com/maps?q=${encodedPostcode}&t=m&z=6&output=embed&iwloc=near`;
    };

    return (
        <Box
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{ display: 'inline-block', cursor: 'pointer', position: 'relative' }}
        >
            {children}
            {showMap && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: position.x + 15,
                        top: position.y - 150,
                        zIndex: 9999,
                        width: 300,
                        height: 200,
                        borderRadius: 'md',
                        overflow: 'hidden',
                        boxShadow: 'lg',
                        border: '2px solid',
                        borderColor: 'primary.500',
                        bgcolor: 'background.surface',
                        pointerEvents: 'none',
                    }}
                >
                    <iframe
                        src={getMapUrl(postcode)}
                        width="300"
                        height="200"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map for ${postcode}`}
                    />
                </Box>
            )}
        </Box>
    );
};

// Time ago helper
const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''}, ${diffHours % 24} hours ago`;
    }
    if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''}, ${Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))} minutes ago`;
    }
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
};

// Ad platform color mapping
const getPlatformColor = (platform: string): 'primary' | 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (platform?.toLowerCase()) {
        case 'facebook':
            return 'primary';
        case 'google':
            return 'success';
        case 'bark':
            return 'warning';
        default:
            return 'neutral';
    }
};

export default function UnassignedLeads() {
    const [leads, setLeads] = useState<UnassignedLead[]>([]);
    const [franchises, setFranchises] = useState<Franchise[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedFranchises, setSelectedFranchises] = useState<{ [key: number]: Franchise | null }>({});
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
    const [hiddenIds, setHiddenIds] = useState<Set<number>>(() => {
        const stored = localStorage.getItem('hiddenUnassignedLeads');
        return stored ? new Set(JSON.parse(stored)) : new Set();
    });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; color: 'success' | 'danger' }>({
        open: false,
        message: '',
        color: 'success'
    });

    // Pagination state
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [leadsData, franchisesResponse] = await Promise.all([
                ApiService.getUnassignedLeads(),
                GetAllFranchises()
            ]);
            setLeads(leadsData);
            setFranchises(franchisesResponse.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            setSnackbar({ open: true, message: 'Failed to load data', color: 'danger' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFranchiseSelect = (customerId: number, franchise: Franchise | null) => {
        setSelectedFranchises(prev => ({ ...prev, [customerId]: franchise }));
    };

    const handleHideLead = (customerId: number) => {
        const newHidden = new Set(hiddenIds).add(customerId);
        setHiddenIds(newHidden);
        localStorage.setItem('hiddenUnassignedLeads', JSON.stringify([...newHidden]));
        setSnackbar({ open: true, message: 'Lead hidden', color: 'success' });
    };

    const handleAssignFranchise = async (lead: UnassignedLead, franchiseOverride?: Franchise) => {
        const franchise = franchiseOverride || selectedFranchises[lead.customer_id];
        if (!franchise) {
            setSnackbar({ open: true, message: 'Please select a franchise first', color: 'danger' });
            return;
        }

        setProcessingIds(prev => new Set(prev).add(lead.customer_id));
        try {
            await ApiService.assignFranchiseByCustomer(lead.customer_id, franchise.id);
            setSnackbar({
                open: true,
                message: `Customer assigned to ${franchise.location}`,
                color: 'success'
            });
            setLeads(prev => prev.filter(l => l.customer_id !== lead.customer_id));
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Assignment failed',
                color: 'danger'
            });
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(lead.customer_id);
                return next;
            });
        }
    };

    // Filter out hidden leads and paginate
    const visibleLeads = leads.filter(lead => !hiddenIds.has(lead.customer_id));
    const paginatedLeads = visibleLeads.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    const pageCount = visibleLeads.length;
    const getLabelDisplayedRowsTo = () => Math.min(pageCount, (page + 1) * rowsPerPage);

    function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number }) {
        return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
    }

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                        {visibleLeads.length} Unassigned Lead{visibleLeads.length !== 1 ? 's' : ''}
                        {hiddenIds.size > 0 && (
                            <Typography component="span" level="body-sm" textColor="text.tertiary" sx={{ ml: 1 }}>
                                ({hiddenIds.size} hidden)
                            </Typography>
                        )}
                    </Typography>
                    <Typography level="body-sm" textColor="text.tertiary">
                        Hover over postcode to see map location. Select a franchise to assign.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="sm"
                    onClick={fetchData}
                    disabled={loading}
                    startDecorator={<Refresh />}
                >
                    Refresh
                </Button>
            </Box>

            {leads.length === 0 && !loading ? (
                <Sheet
                    variant="soft"
                    sx={{
                        borderRadius: 'md',
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <CheckCircle sx={{ fontSize: 48, color: 'success.500', mb: 2 }} />
                    <Typography level="h4">All caught up!</Typography>
                    <Typography level="body-md" textColor="text.tertiary">
                        No unassigned leads at the moment.
                    </Typography>
                </Sheet>
            ) : (
                <Sheet sx={{ overflow: 'auto' }}>
                    <Table aria-label="unassigned leads table">
                        <thead>
                            <tr>
                                <th style={{ width: '120px' }}>Source</th>
                                <th style={{ width: '200px' }}>Created</th>
                                <th>Customer</th>
                                <th style={{ width: '150px' }}>Campaign</th>
                                <th style={{ width: '100px' }}>Postcode</th>
                                <th style={{ width: '280px' }}>Assign To Franchise</th>
                                <th style={{ width: '60px' }} aria-label="actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                        <CircularProgress />
                                    </td>
                                </tr>
                            ) : (
                                paginatedLeads.map((lead) => {
                                    const isProcessing = processingIds.has(lead.customer_id);
                                    const selectedFranchise = selectedFranchises[lead.customer_id];

                                    return (
                                        <tr key={lead.customer_id}>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={getPlatformColor(lead.advertisement_platform)}
                                                >
                                                    {lead.advertisement_platform}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Chip color="primary" size="sm">
                                                    {getTimeAgo(lead.created_at)}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" fontWeight="lg">
                                                    {lead.customer_name}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" textColor="text.tertiary">
                                                    {lead.campaign_name || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <PostcodeMapHover postcode={lead.postcode}>
                                                    <Chip
                                                        size="sm"
                                                        variant="solid"
                                                        color="warning"
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        {lead.outward_postcode || 'N/A'}
                                                    </Chip>
                                                </PostcodeMapHover>
                                            </td>
                                            <td>
                                                <Autocomplete
                                                    size="sm"
                                                    placeholder="Search franchise..."
                                                    options={franchises}
                                                    getOptionLabel={(option) => option.location}
                                                    value={selectedFranchise || null}
                                                    onChange={(_, value) => {
                                                        handleFranchiseSelect(lead.customer_id, value);
                                                        if (value) {
                                                            handleAssignFranchise(lead, value);
                                                        }
                                                    }}
                                                    disabled={isProcessing}
                                                    sx={{ minWidth: 240 }}
                                                    slotProps={{
                                                        input: {
                                                            autoComplete: 'off',
                                                        },
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => handleHideLead(lead.customer_id)}
                                                    disabled={isProcessing}
                                                >
                                                    <VisibilityOff sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={7}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                        <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                            {labelDisplayedRows({
                                                from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                                to: getLabelDisplayedRowsTo(),
                                                count: pageCount
                                            })}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="sm"
                                                color="neutral"
                                                variant="outlined"
                                                disabled={page === 0}
                                                onClick={() => setPage(page - 1)}
                                                sx={{ bgcolor: 'background.surface' }}
                                            >
                                                <KeyboardArrowLeft />
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                color="neutral"
                                                variant="outlined"
                                                disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1}
                                                onClick={() => setPage(page + 1)}
                                                sx={{ bgcolor: 'background.surface' }}
                                            >
                                                <KeyboardArrowRight />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </Sheet>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                color={snackbar.color}
                variant="soft"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                startDecorator={snackbar.color === 'success' ? <CheckCircle /> : <Warning />}
            >
                {snackbar.message}
            </Snackbar>
        </>
    );
}
